import React, { useEffect, useState } from "react";
import Layout from "../components/Layout";
import ApiService from "../services/apiService";
import { getSuperAdminData } from "../utils/tokenUtils";
import { changeSuperAdminPassword, updateSuperAdminProfile } from "../services/authService";
import getApiErrorMessage from "../utils/getApiErrorMessage";

export default function Settings() {
  const [user, setUser] = useState(() => getSuperAdminData());
  const [usernameInput, setUsernameInput] = useState(user?.username || "");
  const [isEditingUsername, setIsEditingUsername] = useState(false);
  const [savingUsername, setSavingUsername] = useState(false);
  const [profileMessage, setProfileMessage] = useState("");
  const [profileError, setProfileError] = useState("");
  const [passwordForm, setPasswordForm] = useState({ currentPassword: "", newPassword: "", confirmPassword: "" });
  const [passwordMessage, setPasswordMessage] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [savingPassword, setSavingPassword] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [cleanupError, setCleanupError] = useState("");
  const [cleanupPreview, setCleanupPreview] = useState(null);
  const [savedShopDocuments, setSavedShopDocuments] = useState([]);
  const [savedDocumentsLoading, setSavedDocumentsLoading] = useState(false);
  const [savedDocumentsError, setSavedDocumentsError] = useState("");
  const [deletingDocumentId, setDeletingDocumentId] = useState(null);
  const [toastMessage, setToastMessage] = useState("");
  const [toastType, setToastType] = useState("success");
  const [activeSection, setActiveSection] = useState("general");
  const [isCompact, setIsCompact] = useState(() => typeof window !== "undefined" ? window.innerWidth < 1174 : false);

  const settingsSections = [
    { id: "general", label: "General" },
    { id: "security", label: "Security" },
    { id: "cleanup", label: "Document Cleanup" },
  ];

  useEffect(() => {
    const currentUser = getSuperAdminData();
    setUser(currentUser);
    setUsernameInput(currentUser?.username || "");
  }, []);

  const loadSavedShopDocuments = async () => {
    try {
      setSavedDocumentsLoading(true);
      setSavedDocumentsError("");
      const response = await ApiService.getSavedShopDocuments();
      if (!Array.isArray(response?.data)) {
        throw new Error("Invalid saved shop documents response.");
      }
      const documents = response.data;
      setSavedShopDocuments(documents);
    } catch (error) {
      setSavedDocumentsError(getApiErrorMessage(error, "Failed to load saved shop documents."));
    } finally {
      setSavedDocumentsLoading(false);
    }
  };

  useEffect(() => {
    loadSavedShopDocuments();
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsCompact(window.innerWidth < 1174);
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (!toastMessage) return undefined;

    const timeoutId = setTimeout(() => {
      setToastMessage("");
      setToastType("success");
    }, 3000);

    return () => clearTimeout(timeoutId);
  }, [toastMessage]);

  const handlePasswordChange = (event) => {
    setPasswordForm({ ...passwordForm, [event.target.name]: event.target.value });
    setPasswordMessage("");
    setPasswordError("");
  };

  const handleProfileSave = async () => {
    const trimmedUsername = usernameInput.trim();
    if (!trimmedUsername || trimmedUsername.length < 3) {
      setProfileError("Username must be at least 3 characters long.");
      return;
    }

    if (trimmedUsername === user?.username) {
      setIsEditingUsername(false);
      setProfileError("");
      setProfileMessage("Username is already up to date.");
      return;
    }

    setSavingUsername(true);
    setProfileError("");
    setProfileMessage("");

    try {
      const response = await updateSuperAdminProfile(user?.id, { username: trimmedUsername });
      const updatedUser = {
        ...user,
        ...response?.data?.super_admin,
        username: response?.data?.super_admin?.username || trimmedUsername,
      };

      localStorage.setItem("superAdminData", JSON.stringify(updatedUser));
      setUser(updatedUser);
      setUsernameInput(updatedUser.username);
      setProfileMessage(response?.data?.message || "Username updated successfully.");
      setIsEditingUsername(false);
    } catch (error) {
      setProfileError(error.response?.data?.message || "Failed to update username.");
    } finally {
      setSavingUsername(false);
    }
  };

  const handlePasswordSubmit = async (event) => {
    event.preventDefault();
    setPasswordMessage("");
    setPasswordError("");

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordError("New password and confirmation do not match");
      return;
    }

    setSavingPassword(true);
    try {
      const response = await changeSuperAdminPassword(user?.id, {
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword
      });
      setPasswordMessage(response.data.message);
      setPasswordForm({ currentPassword: "", newPassword: "", confirmPassword: "" });
      setShowPasswordModal(false);
    } catch (error) {
      setPasswordError(error.response?.data?.message || "Failed to change password");
    } finally {
      setSavingPassword(false);
    }
  };

  const handleDeleteSavedDocument = async (documentId) => {
    if (!documentId) return;

    const document = (savedShopDocuments || []).find((item) => String(item.document_id) === String(documentId));
    const isUnlinked = (document?.status || "").toLowerCase() === "orphaned" || (document?.status || "").toLowerCase() === "unlinked";
    if (!document || !isUnlinked) {
      setCleanupError("Only unlinked files can be deleted from this list.");
      return;
    }

    if (!window.confirm("Are you sure you want to delete this unlinked shop document?")) {
      return;
    }

    try {
      setDeletingDocumentId(documentId);
      const response = await ApiService.deleteSavedShopDocument(documentId);
      if (!response?.success) {
        throw new Error(response?.message || "Failed to delete document.");
      }
      await loadSavedShopDocuments();
      const successMessage = response.message || "Unlinked document deleted successfully.";
      setCleanupError("");
      setToastMessage(successMessage);
      setToastType("success");
    } catch (error) {
      const serverMessage = error?.response?.data?.message || error?.message || "Failed to delete document.";
      setCleanupError(serverMessage);
      setToastMessage(serverMessage);
      setToastType("error");
    } finally {
      setDeletingDocumentId(null);
    }
  };

  const handleDeleteAllOrphanedDocuments = async () => {
    if (!window.confirm("Delete all unlinked files from the uploads folder? This cannot be undone.")) {
      return;
    }

    try {
      setSavedDocumentsLoading(true);
      const response = await ApiService.deleteAllOrphanedDocuments();
      if (!response?.success) {
        throw new Error(response?.message || "Failed to delete unlinked files.");
      }
      await loadSavedShopDocuments();
      const successMessage = response.message || "Unlinked documents deleted successfully.";
      setCleanupError("");
      setToastMessage(successMessage);
      setToastType("success");
    } catch (error) {
      const serverMessage = error?.response?.data?.message || error?.message || "Failed to delete unlinked files.";
      setCleanupError(serverMessage);
      setToastMessage(serverMessage);
      setToastType("error");
    } finally {
      setSavedDocumentsLoading(false);
    }
  };

  const renderContent = () => {
    if (activeSection === "general") {
      return (
        <div style={{ background: "#fff", border: "1px solid #e8edf4", borderRadius: 16, overflow: "hidden" }}>
          <div style={{ borderBottom: "1px solid #edf2f7", padding: isCompact ? "16px 18px 12px" : "18px 24px 14px" }}>
            <h2 style={{ margin: 0, fontSize: isCompact ? 18 : 20, color: "#172033" }}>General</h2>
          </div>

          <div style={{ display: "flex", alignItems: isCompact ? "flex-start" : "center", justifyContent: "space-between", gap: 16, padding: isCompact ? "16px 18px" : "18px 24px", borderBottom: "1px solid #edf2f7", flexWrap: "wrap", flexDirection: isCompact ? "column" : "row" }}>
            <div style={{ minWidth: 160, flex: 1, width: "100%" }}>
              <div style={{ fontSize: 12, color: "#64748b", textTransform: "uppercase", letterSpacing: ".08em", fontWeight: 800, marginBottom: 6 }}>Username</div>
              {!isEditingUsername ? (
                <div style={{ fontSize: 15, color: "#172033", fontWeight: 600 }}>{user?.username || "SuperAdmin"}</div>
              ) : (
                <input value={usernameInput} onChange={(event) => setUsernameInput(event.target.value)} style={{ width: "100%", maxWidth: 420, padding: "10px 12px", borderRadius: 10, border: "1px solid #dfeaf6", background: "#fff", color: "#172033", boxSizing: "border-box", fontSize: 14 }} />
              )}
            </div>

            {!isEditingUsername ? (
              <button type="button" onClick={() => { setProfileError(""); setProfileMessage(""); setIsEditingUsername(true); }} style={{
                border: "1px solid #dfeaf6",
                background: "#f8fbff",
                color: "#2563a8",
                borderRadius: 10,
                padding: "9px 14px",
                fontWeight: 700,
                cursor: "pointer",
                fontSize: 13
              }}>
                Edit
              </button>
            ) : (
              <button type="button" disabled={savingUsername} onClick={handleProfileSave} style={{
                border: "none",
                background: savingUsername ? "#93c5fd" : "#2563a8",
                color: "#fff",
                borderRadius: 10,
                padding: "9px 14px",
                fontWeight: 700,
                cursor: savingUsername ? "not-allowed" : "pointer",
                fontSize: 13
              }}>
                {savingUsername ? "Saving..." : "Save"}
              </button>
            )}
          </div>

          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, padding: "18px 24px", flexWrap: "wrap" }}>
            <div style={{ minWidth: 160, flex: 1 }}>
              <div style={{ fontSize: 12, color: "#64748b", textTransform: "uppercase", letterSpacing: ".08em", fontWeight: 800, marginBottom: 6 }}>Email</div>
              <div style={{ fontSize: 15, color: "#172033", fontWeight: 600 }}>{user?.email || "No email available"}</div>
            </div>
          </div>
        </div>
      );
    }

    if (activeSection === "security") {
      return (
        <div style={{ background: "#fff", border: "1px solid #e8edf4", borderRadius: 16, overflow: "hidden" }}>
          <div style={{ borderBottom: "1px solid #edf2f7", padding: isCompact ? "16px 18px 12px" : "18px 24px 14px" }}>
            <h2 style={{ margin: 0, fontSize: isCompact ? 18 : 20, color: "#172033" }}>Security</h2>
          </div>

          <div style={{ display: "flex", alignItems: isCompact ? "flex-start" : "center", justifyContent: "space-between", gap: 16, padding: isCompact ? "16px 18px" : "18px 24px", flexWrap: "wrap", flexDirection: isCompact ? "column" : "row" }}>
            <div style={{ minWidth: 160, flex: 1, width: "100%" }}>
              <div style={{ fontSize: 12, color: "#64748b", textTransform: "uppercase", letterSpacing: ".08em", fontWeight: 800, marginBottom: 6 }}>Password</div>
              <div style={{ fontSize: 15, color: "#172033", fontWeight: 600 }}>••••••••</div>
            </div>
            <button type="button" onClick={() => { setPasswordError(""); setPasswordMessage(""); setShowPasswordModal(true); }} style={{
              border: "1px solid #dfeaf6",
              background: "#f8fbff",
              color: "#2563a8",
              borderRadius: 10,
              padding: "9px 14px",
              fontWeight: 700,
              cursor: "pointer",
              fontSize: 13
            }}>
              Change Password
            </button>
          </div>
        </div>
      );
    }

    return (
      <div style={{ background: "#fff", border: "1px solid #e8edf4", borderRadius: 16, overflow: "hidden" }}>
        <div style={{ borderBottom: "1px solid #edf2f7", padding: isCompact ? "16px 18px 12px" : "18px 24px 14px" }}>
          <h2 style={{ margin: 0, fontSize: isCompact ? 18 : 20, color: "#172033" }}>Document Cleanup</h2>
        </div>

        <div style={{ padding: isCompact ? "16px 18px" : "24px" }}>
          {cleanupError && <div style={{ marginTop: 16, padding: "10px 12px", borderRadius: 10, background: "#fef2f2", color: "#991b1b", border: "1px solid #fecaca", fontSize: 13 }}>{cleanupError}</div>}
          {cleanupPreview && cleanupPreview.candidateCount > 0 && (
            <div style={{ marginTop: 16, padding: "12px 14px", borderRadius: 10, background: "#fff7ed", border: "1px solid #fed7aa", color: "#9a5c00", fontSize: 13 }}>
              Preview: {cleanupPreview.candidateCount} stale rejected document(s) available for cleanup.
            </div>
          )}

          <div style={{ marginTop: 28, borderTop: "1px solid #edf2f7", paddingTop: 22 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
              <h3 style={{ margin: 0, fontSize: 18, color: "#172033" }}>Saved Shop Documents</h3>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                <div style={{
                  background: "#fff7ed",
                  color: "#9a5c00",
                  border: "1px solid #fed7aa",
                  borderRadius: 999,
                  padding: "6px 10px",
                  fontSize: 11,
                  fontWeight: 800,
                }}>
                  {savedShopDocuments.filter((document) => {
                    const status = (document.status || "").toLowerCase();
                    return status === "orphaned" || status === "unlinked";
                  }).length} unlinked
                </div>
                <button type="button" onClick={loadSavedShopDocuments} disabled={savedDocumentsLoading} style={{
                  border: "1px solid #dfeaf6",
                  background: savedDocumentsLoading ? "#e2e8f0" : "#f8fbff",
                  color: "#2563a8",
                  borderRadius: 10,
                  padding: "8px 12px",
                  fontWeight: 700,
                  cursor: savedDocumentsLoading ? "not-allowed" : "pointer",
                  fontSize: 12
                }}>
                  {savedDocumentsLoading ? "Refreshing..." : "Refresh"}
                </button>
                <button type="button" onClick={handleDeleteAllOrphanedDocuments} disabled={savedDocumentsLoading} style={{
                  border: "1px solid #fed7aa",
                  background: savedDocumentsLoading ? "#e2e8f0" : "#fff7ed",
                  color: "#9a5c00",
                  borderRadius: 10,
                  padding: "8px 12px",
                  fontWeight: 700,
                  cursor: savedDocumentsLoading ? "not-allowed" : "pointer",
                  fontSize: 12
                }}>
                  Delete All Unlinked
                </button>
              </div>
            </div>

            {savedDocumentsError && (
              <div style={{ marginBottom: 12, padding: "10px 12px", borderRadius: 10, background: "#fef2f2", color: "#991b1b", border: "1px solid #fecaca", fontSize: 13 }}>
                {savedDocumentsError}
              </div>
            )}

            {savedDocumentsLoading ? (
              <div style={{ color: "#64748b", fontSize: 14 }}>Loading saved documents...</div>
            ) : savedShopDocuments.length === 0 ? (
              <div style={{ color: "#64748b", fontSize: 14 }}>No saved shop documents found.</div>
            ) : (
              <div style={{ display: "grid", gap: 12 }}>
                {savedShopDocuments.map((document) => {
                  const status = (document.status || "pending").toLowerCase();
                  const isApproved = status === "approved";
                  const isRejected = status === "rejected";
                  const isPending = status === "pending";
                  const isUnlinked = status === "orphaned" || status === "unlinked";
                  const badgeStyle = isApproved
                    ? { background: "#ecfdf5", color: "#166534", border: "1px solid #bbf7d0" }
                    : isRejected
                      ? { background: "#fef2f2", color: "#991b1b", border: "1px solid #fecaca" }
                      : isPending
                        ? { background: "#eff6ff", color: "#1d4ed8", border: "1px solid #bfdbfe" }
                        : isUnlinked
                          ? { background: "#fff7ed", color: "#9a5c00", border: "1px solid #fed7aa" }
                          : { background: "#f8fafc", color: "#475569", border: "1px solid #e2e8f0" };

                  return (
                    <div key={document.document_id} style={{
                      display: "grid",
                      gridTemplateColumns: isCompact ? "1fr" : "1.2fr 1fr auto auto",
                      gap: 12,
                      alignItems: "center",
                      border: isApproved || isRejected || isPending ? "1px solid #cbd5e1" : "1px solid #e2e8f0",
                      background: isApproved ? "#ecfdf5" : isRejected ? "#fef2f2" : isPending ? "#eff6ff" : "#fff",
                      borderRadius: 12,
                      padding: isCompact ? "12px" : "14px 16px",
                    }}>
                      <div>
                        <div style={{ fontSize: 13, color: isApproved ? "#166534" : isRejected ? "#991b1b" : isPending ? "#1d4ed8" : "#64748b", textTransform: "uppercase", letterSpacing: ".08em", fontWeight: 800, marginBottom: 4 }}>{document.document_type || "Document"}</div>
                        <div style={{ fontWeight: 700, color: isApproved ? "#14532d" : isRejected ? "#7f1d1d" : isPending ? "#1e3a8a" : "#172033" }}>{document.shop_name || `Shop #${document.shop_id}`}</div>
                        <div style={{ fontSize: 12, color: isApproved ? "#166534" : isRejected ? "#991b1b" : isPending ? "#1d4ed8" : "#64748b", marginTop: 4 }}>{document.original_filename || "Uploaded document"}</div>
                      </div>

                      <div style={{ fontSize: 12, color: isApproved ? "#166534" : isRejected ? "#991b1b" : isPending ? "#1d4ed8" : "#64748b", fontWeight: isApproved || isRejected || isPending ? 700 : 400 }}>
                        {document.uploaded_at ? new Date(document.uploaded_at).toLocaleString() : (isUnlinked ? "Unlinked file" : "Unknown date")}
                      </div>

                      <div style={{ display: "flex", justifyContent: isCompact ? "flex-start" : "center" }}>
                        <span style={{ ...badgeStyle, borderRadius: 999, padding: "6px 10px", fontSize: 11, fontWeight: 900, textTransform: "capitalize", letterSpacing: ".02em" }}>
                          {isApproved ? "Approved" : isRejected ? "Rejected" : isPending ? "Pending" : isUnlinked ? "Unlinked" : status}
                        </span>
                      </div>

                      {isUnlinked ? (
                        <button type="button" onClick={() => handleDeleteSavedDocument(document.document_id)} disabled={deletingDocumentId === document.document_id} style={{
                          border: "1px solid #fecaca",
                          background: deletingDocumentId === document.document_id ? "#fca5a5" : "#fff1f2",
                          color: "#991b1b",
                          borderRadius: 10,
                          padding: "8px 12px",
                          fontWeight: 800,
                          cursor: deletingDocumentId === document.document_id ? "not-allowed" : "pointer",
                          fontSize: 12
                        }}>
                          {deletingDocumentId === document.document_id ? "Deleting..." : "Delete"}
                        </button>
                      ) : (
                        <div style={{
                          border: "1px solid #e2e8f0",
                          background: "#f8fafc",
                          color: "#64748b",
                          borderRadius: 10,
                          padding: "8px 12px",
                          fontWeight: 700,
                          fontSize: 12,
                          textAlign: "center",
                          minWidth: 90,
                        }}>
                          Protected
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <Layout>
      <div style={{ maxWidth: 1100, margin: "24px auto", padding: "0 20px 28px", boxSizing: "border-box" }}>
        <div style={{ marginBottom: 18 }}>
          <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: ".12em", color: "#2563a8", fontWeight: 800, marginBottom: 8 }}>
            Account
          </div>
          <h1 style={{ margin: 0, fontSize: 30, color: "#172033", letterSpacing: "-0.03em" }}>Settings</h1>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: isCompact ? "1fr" : "230px minmax(0, 1fr)", gap: 20, alignItems: "start", width: "100%", boxSizing: "border-box" }}>
          <aside style={{ background: "#fff", border: "1px solid #e8edf4", borderRadius: 16, padding: 12, position: isCompact ? "static" : "sticky", top: 20, boxSizing: "border-box", display: isCompact ? "grid" : "block", gridTemplateColumns: isCompact ? "repeat(3, minmax(0, 1fr))" : "1fr" }}>
            {settingsSections.map((section) => {
              const isActive = activeSection === section.id;
              return (
                <button
                  key={section.id}
                  type="button"
                  onClick={() => setActiveSection(section.id)}
                  style={{
                    display: "block",
                    width: "100%",
                    border: "none",
                    background: isActive ? "#e0f2fe" : "transparent",
                    color: isActive ? "#0f172a" : "#475569",
                    borderRadius: 10,
                    padding: isCompact ? "10px 8px" : "12px 14px",
                    textAlign: isCompact ? "center" : "left",
                    fontSize: isCompact ? 12 : 14,
                    fontWeight: 700,
                    cursor: "pointer",
                    marginBottom: 6,
                  }}
                >
                  {section.label}
                </button>
              );
            })}
          </aside>

          <main style={{ minWidth: 0, width: "100%" }}>
            {renderContent()}
          </main>
        </div>

        {profileMessage && <div style={{ marginTop: 12, padding: "10px 12px", borderRadius: 10, background: "#ecfdf5", color: "#166534", border: "1px solid #bbf7d0", fontSize: 13 }}>{profileMessage}</div>}
        {profileError && <div style={{ marginTop: 12, padding: "10px 12px", borderRadius: 10, background: "#fef2f2", color: "#991b1b", border: "1px solid #fecaca", fontSize: 13 }}>{profileError}</div>}
        {passwordMessage && <div style={{ marginTop: 12, padding: "10px 12px", borderRadius: 10, background: "#ecfdf5", color: "#166534", border: "1px solid #bbf7d0", fontSize: 13 }}>{passwordMessage}</div>}
        {passwordError && <div style={{ marginTop: 12, padding: "10px 12px", borderRadius: 10, background: "#fef2f2", color: "#991b1b", border: "1px solid #fecaca", fontSize: 13 }}>{passwordError}</div>}

        {toastMessage && (
          <div style={{
            position: "fixed",
            right: 20,
            bottom: 20,
            zIndex: 1200,
            maxWidth: 360,
            padding: "12px 16px",
            borderRadius: 12,
            background: toastType === "success" ? "#ecfdf5" : "#fef2f2",
            border: `1px solid ${toastType === "success" ? "#bbf7d0" : "#fecaca"}`,
            color: toastType === "success" ? "#166534" : "#991b1b",
            boxShadow: "0 10px 25px rgba(15, 23, 42, 0.12)",
            fontSize: 13,
            fontWeight: 700,
          }}>
            {toastMessage}
          </div>
        )}

        {showPasswordModal && (
          <div onClick={() => !savingPassword && setShowPasswordModal(false)} style={{ position: "fixed", inset: 0, background: "rgba(15, 23, 42, 0.5)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20, zIndex: 1000 }}>
            <div onClick={(event) => event.stopPropagation()} style={{ width: "100%", maxWidth: 460, background: "#fff", borderRadius: 16, padding: 24, boxShadow: "0 20px 50px rgba(15, 23, 42, 0.22)", border: "1px solid #e5edf7" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18 }}>
                <h2 style={{ margin: 0, fontSize: 20, color: "#172033" }}>Change Password</h2>
                <button type="button" onClick={() => setShowPasswordModal(false)} disabled={savingPassword} aria-label="Close change password dialog" style={{ border: "none", background: "transparent", color: "#64748b", fontSize: 26, cursor: savingPassword ? "not-allowed" : "pointer", lineHeight: 1, padding: 0 }}>x</button>
              </div>

              <form onSubmit={handlePasswordSubmit}>
                <div style={{ marginBottom: 14 }}>
                  <label style={{ display: "block", fontSize: 12, marginBottom: 8, color: "#334155", fontWeight: 700 }}>Current Password</label>
                  <input name="currentPassword" type="password" value={passwordForm.currentPassword} onChange={handlePasswordChange} required autoFocus style={{ width: "100%", padding: "11px 12px", borderRadius: 10, border: "1px solid #dfeaf6", boxSizing: "border-box", fontSize: 14 }} />
                </div>
                <div style={{ marginBottom: 14 }}>
                  <label style={{ display: "block", fontSize: 12, marginBottom: 8, color: "#334155", fontWeight: 700 }}>New Password</label>
                  <input name="newPassword" type="password" value={passwordForm.newPassword} onChange={handlePasswordChange} minLength={8} required style={{ width: "100%", padding: "11px 12px", borderRadius: 10, border: "1px solid #dfeaf6", boxSizing: "border-box", fontSize: 14 }} />
                </div>
                <div style={{ marginBottom: 18 }}>
                  <label style={{ display: "block", fontSize: 12, marginBottom: 8, color: "#334155", fontWeight: 700 }}>Confirm New Password</label>
                  <input name="confirmPassword" type="password" value={passwordForm.confirmPassword} onChange={handlePasswordChange} minLength={8} required style={{ width: "100%", padding: "11px 12px", borderRadius: 10, border: "1px solid #dfeaf6", boxSizing: "border-box", fontSize: 14 }} />
                </div>
                <button type="submit" disabled={savingPassword} style={{ width: "100%", padding: "11px 14px", borderRadius: 10, background: savingPassword ? "#93c5fd" : "#2563a8", color: "#fff", border: "none", cursor: savingPassword ? "not-allowed" : "pointer", fontWeight: 700, fontSize: 14 }}>
                  {savingPassword ? "Updating..." : "Update Password"}
                </button>
                {passwordError && <p style={{ color: "#dc2626", fontSize: 13, margin: "14px 0 0" }}>{passwordError}</p>}
              </form>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
